import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  CalendarDays,
  Phone,
  MapPin,
  X,
  Sparkles,
  DollarSign,
  Users,
  Clock,
  Trash2,
  PlusCircle,
  Inbox,
  Mail,
  Search,
  Images,
  Upload,
  Eye,
  EyeOff,
  CheckCircle2,
  Undo2,
  Tag,
  Star,
  MessageSquareQuote,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import ScheduleManager from "@/components/ScheduleManager";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ddd_admin_token";

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function LoginCard({ onLoggedIn }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setToken(res.data.token);
      toast.success("Welcome, Dan!");
      onLoggedIn();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FDFBF7] px-4">
      <form
        onSubmit={onSubmit}
        className="comic-card-static p-7 max-w-md w-full"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-5">
          <span className="inline-block w-10 h-10 rounded-full bg-[#00E676] border-[3px] border-[#0B132B] grid place-items-center">
            <Sparkles className="w-5 h-5 text-[#0B132B]" />
          </span>
          <div>
            <div className="font-['Bebas_Neue'] text-3xl tracking-wide leading-none">
              Admin Login
            </div>
            <div className="text-sm text-[#0B132B]/70">
              Daniel's Dirty Deeds · Owner access
            </div>
          </div>
        </div>
        <label className="block text-sm font-semibold mb-1">Password</label>
        <div className="relative">
          <input
            data-testid="admin-password-input"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="comic-input"
            style={{ paddingRight: "2.75rem" }}
            placeholder="Enter admin password"
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            data-testid="admin-password-toggle"
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[#FFF4C2] text-[#0B132B]"
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        </div>
        <button
          type="submit"
          disabled={submitting || !password}
          className="mt-5 w-full comic-btn comic-btn-action"
          data-testid="admin-login-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Logging in…
            </>
          ) : (
            "Log In"
          )}
        </button>
        <a href="/" className="block text-center mt-4 text-sm text-[#0B132B]/70 underline">
          ← Back to home
        </a>
      </form>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tint }) {
  return (
    <div className="comic-card-static p-4 flex items-center gap-4">
      <div
        className="w-12 h-12 rounded-full border-[3px] border-[#0B132B] grid place-items-center"
        style={{ background: tint }}
      >
        <Icon className="w-6 h-6 text-[#0B132B]" />
      </div>
      <div>
        <div className="font-['Bebas_Neue'] text-3xl leading-none">{value}</div>
        <div className="text-xs uppercase tracking-wide text-[#0B132B]/70">{label}</div>
      </div>
    </div>
  );
}

function NotifBadge({ count, testId }) {
  if (!count || count <= 0) return null;
  return (
    <span
      data-testid={testId}
      className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3D00] text-white text-[10px] font-bold border-[2px] border-[#0B132B] inline-flex items-center justify-center shadow-[2px_2px_0_#0B132B]"
    >
      {count > 9 ? "9+" : count}
    </span>
  );
}

function AppointmentRow({ a, onCancel, onDelete, onComplete, onReopen, onReschedule }) {
  const start = new Date(a.start_at);
  const isCancelled = a.status === "cancelled";
  const isCompleted = a.status === "completed";
  const isPaid = a.payment_status === "paid";
  const cityClean = a.city ? `${a.city}, CT` : "";
  const svc = a.service_type && a.service_type !== "bin_cleaning" ? a.service_type : null;
  const svcLabel = svc
    ? svc.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  return (
    <div
      data-testid={`admin-appt-row-${a.id}`}
      onMouseEnter={() => onSeen && onSeen(a.id)}
      className={`comic-card-static p-4 ${isCancelled ? "opacity-60" : ""} ${
        isCompleted ? "bg-[#E8F8EE]" : ""
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-['Bebas_Neue'] text-2xl leading-tight">
            {start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
            <span className="text-base ml-2 text-[#0B132B]/70">
              {svc ? `(${svcLabel})` : `(${a.num_bins} bin${a.num_bins > 1 ? "s" : ""})`}
            </span>
          </div>
          <div className="text-sm font-semibold">{a.customer_name}</div>
          <div className="text-xs text-[#0B132B]/70 flex flex-wrap gap-x-3 gap-y-1 mt-1">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {a.address} · {cityClean}
            </span>
            <a
              href={`tel:${a.customer_phone?.replace(/\D/g, "")}`}
              className="inline-flex items-center gap-1 underline"
            >
              <Phone className="w-3.5 h-3.5" /> {a.customer_phone}
            </a>
          </div>
          {a.service_description && (
            <div className="text-xs mt-1 text-[#0B132B]/80">
              <span className="font-semibold">Job:</span> {a.service_description}
            </div>
          )}
          {a.notes && (
            <div className="text-xs italic mt-1 text-[#0B132B]/80">"{a.notes}"</div>
          )}
          {a.promo_code && (
            <div className="text-[10px] mt-1 inline-block bg-[#00E676] text-[#0B132B] px-2 py-0.5 rounded font-semibold border border-[#0B132B]">
              PROMO: {a.promo_code}
            </div>
          )}
          {a.series_id && (
            <div className="text-[10px] mt-1 ml-1 inline-block bg-[#1E88E5] text-white px-2 py-0.5 rounded font-semibold">
              RECURRING #{a.series_index + 1}/{a.series_size}
            </div>
          )}
          {a.out_of_area && (
            <div className="text-[10px] mt-1 ml-1 inline-block bg-[#FF3D00] text-white px-2 py-0.5 rounded font-semibold">
              OUT OF AREA · +$5
            </div>
          )}
          {a.created_by === "admin" && (
            <div className="text-[10px] mt-1 ml-1 inline-block bg-[#FFC107] text-[#0B132B] px-2 py-0.5 rounded font-semibold border border-[#0B132B]">
              ADMIN-CREATED
            </div>
          )}
          {isCompleted && (
            <div className="text-[10px] mt-1 ml-1 inline-block bg-[#0B132B] text-white px-2 py-0.5 rounded font-semibold">
              COMPLETED
              {a.completed_at &&
                ` · ${new Date(a.completed_at).toLocaleDateString()}`}
            </div>
          )}
          {a.created_at && (
            <div
              data-testid={`admin-appt-booked-at-${a.id}`}
              className="text-[10px] text-[#0B132B]/60 mt-2"
            >
              Booked {new Date(a.created_at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-right">
            <div className="font-['Bebas_Neue'] text-2xl text-[#1E88E5]">
              ${Number(a.total_amount).toFixed(2)}
            </div>
            <div className="text-[11px] uppercase tracking-wide">
              {a.payment_method === "stripe"
                ? isPaid
                  ? "Paid online"
                  : "Awaiting payment"
                : isPaid
                  ? "Paid cash"
                  : "Cash on service"}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {!isCancelled && !isCompleted && (
              <>
                <button
                  data-testid={`admin-complete-btn-${a.id}`}
                  onClick={() => onComplete(a)}
                  className="text-xs font-bold inline-flex items-center gap-1 bg-[#00E676] text-[#0B132B] border-[2px] border-[#0B132B] rounded px-2 py-1 shadow-[2px_2px_0_#0B132B] hover:bg-[#69ffa1]"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Mark Complete
                </button>
                <button
                  data-testid={`admin-reschedule-btn-${a.id}`}
                  onClick={() => onReschedule(a)}
                  className="text-xs font-bold inline-flex items-center gap-1 bg-white border-[2px] border-[#0B132B] rounded px-2 py-1 shadow-[2px_2px_0_#0B132B] hover:bg-[#FFF4C2]"
                >
                  <CalendarDays className="w-3.5 h-3.5" /> Reschedule
                </button>
                <button
                  data-testid={`admin-cancel-btn-${a.id}`}
                  onClick={() => onCancel(a)}
                  className="text-xs font-semibold inline-flex items-center gap-1 text-[#FF3D00] underline"
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
              </>
            )}
            {isCompleted && (
              <button
                data-testid={`admin-reopen-btn-${a.id}`}
                onClick={() => onReopen(a)}
                className="text-xs font-semibold inline-flex items-center gap-1 text-[#0B132B]/80 underline"
              >
                <Undo2 className="w-3 h-3" /> Reopen
              </button>
            )}
            {isCancelled && (
              <span className="text-xs font-semibold text-[#FF3D00] uppercase">Cancelled</span>
            )}
            <button
              data-testid={`admin-delete-btn-${a.id}`}
              onClick={() => onDelete(a)}
              className="text-xs font-semibold inline-flex items-center gap-1 text-[#0B132B]/70 underline hover:text-[#FF3D00]"
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("bookings"); // bookings | schedule | requests | promo | reviews
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("upcoming"); // upcoming | today | all | completed
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);

  // ---- Notification badges ----
  // "Last seen" timestamps mean only NEW items since the admin's last check show up as badges.
  // On first ever visit, we seed with "now" so existing appointments don't show as unseen.
  const [bookingsLastSeen, setBookingsLastSeen] = useState(() => {
    let v = localStorage.getItem("admin_bookings_last_seen");
    if (!v) {
      v = new Date().toISOString();
      try {
        localStorage.setItem("admin_bookings_last_seen", v);
      } catch {
        /* ignore */
      }
    }
    return v;
  });
  const [pendingReviewsCount, setPendingReviewsCount] = useState(0);

  // Booking badge = appointments created AFTER the last time admin tapped the Bookings tab.
  const unseenBookingsCount = appointments.filter(
    (a) =>
      a.created_at &&
      a.created_at > bookingsLastSeen &&
      a.status !== "cancelled" &&
      a.status !== "completed",
  ).length;

  // Fetch pending reviews count for the badge whenever data refreshes.
  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/admin/reviews`, {
        headers: authHeaders(),
        params: { status: "pending" },
      })
      .then((r) => mounted && setPendingReviewsCount((r.data || []).length))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [refreshFlag, tab]);

  const handleSelectTab = (next) => {
    // Tapping the Bookings tab clears the bookings badge.
    if (next === "bookings") {
      const now = new Date().toISOString();
      setBookingsLastSeen(now);
      try {
        localStorage.setItem("admin_bookings_last_seen", now);
      } catch {
        /* ignore */
      }
    }
    // Tapping the Reviews tab clears the reviews badge.
    if (next === "reviews") {
      setPendingReviewsCount(0);
    }
    setTab(next);
  };

  useEffect(() => {
    if (tab !== "bookings") return;
    let mounted = true;
    setLoading(true);
    const params = {};
    const todayStr = formatLocalDate(new Date());
    if (dateFilter === "today") {
      params.date_from = todayStr;
      params.date_to = todayStr;
    } else if (dateFilter === "upcoming") {
      params.date_from = todayStr;
    } else if (dateFilter === "completed") {
      // Show ONLY completed jobs (latest 500)
      params.status = "completed";
      params.include_completed = true;
    }
    Promise.all([
      axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      axios.get(`${API}/admin/appointments`, { headers: authHeaders(), params }),
    ])
      .then(([s, a]) => {
        if (!mounted) return;
        setStats(s.data);
        // Completed filter: sort newest-first instead of by start_at asc
        const list = a.data || [];
        if (dateFilter === "completed") {
          list.sort((x, y) => (y.completed_at || "").localeCompare(x.completed_at || ""));
        }
        setAppointments(list);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setToken(null);
          onLogout();
        } else {
          toast.error("Failed to load dashboard");
        }
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [tab, dateFilter, refreshFlag, onLogout]);

  const handleCancel = async (a) => {
    if (!window.confirm(`Cancel booking for ${a.customer_name}?`)) return;
    try {
      await axios.post(
        `${API}/admin/appointments/${a.id}/cancel`,
        {},
        { headers: authHeaders() },
      );
      toast.success("Booking cancelled");
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not cancel");
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Permanently remove booking for ${a.customer_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/appointments/${a.id}`, {
        headers: authHeaders(),
      });
      toast.success("Booking removed");
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not remove");
    }
  };

  const handleComplete = async (a) => {
    try {
      await axios.post(
        `${API}/admin/appointments/${a.id}/complete`,
        {},
        { headers: authHeaders() },
      );
      toast.success("Marked complete");
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not mark complete");
    }
  };

  const handleReopen = async (a) => {
    try {
      await axios.post(
        `${API}/admin/appointments/${a.id}/reopen`,
        {},
        { headers: authHeaders() },
      );
      toast.success("Reopened — back on the schedule");
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not reopen");
    }
  };

  // Group by date
  const grouped = appointments.reduce((acc, a) => {
    const d = new Date(a.start_at);
    const key = d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
    acc[key] = acc[key] || [];
    acc[key].push(a);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0B132B] text-white border-b-4 border-[#0B132B]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-block w-9 h-9 rounded-full bg-[#00E676] border-[3px] border-[#0B132B] grid place-items-center">
              <Sparkles className="w-4 h-4 text-[#0B132B]" />
            </span>
            <div className="font-['Bebas_Neue'] text-2xl sm:text-3xl tracking-wider">
              <span className="text-[#00E676]">DANIEL'S</span> ADMIN
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="hidden sm:inline-flex comic-btn comic-btn-outline text-sm"
              style={{ padding: "0.45rem 0.8rem", fontSize: "0.95rem", color: "#0B132B" }}
            >
              View site
            </a>
            <button
              data-testid="admin-logout-btn"
              onClick={() => {
                setToken(null);
                onLogout();
              }}
              className="comic-btn comic-btn-action"
              style={{ padding: "0.5rem 1rem", fontSize: "0.95rem" }}
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Tab switcher */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            data-testid="admin-tab-bookings"
            onClick={() => handleSelectTab("bookings")}
            className="slot relative"
            data-selected={tab === "bookings" ? "true" : "false"}
            style={{ padding: "0.55rem 1rem" }}
          >
            <CalendarDays className="w-4 h-4 inline mr-1" /> Bookings
            <NotifBadge count={unseenBookingsCount} testId="badge-bookings" />
          </button>
          <button
            data-testid="admin-tab-schedule"
            onClick={() => handleSelectTab("schedule")}
            className="slot"
            data-selected={tab === "schedule" ? "true" : "false"}
            style={{ padding: "0.55rem 1rem" }}
          >
            <Clock className="w-4 h-4 inline mr-1" /> Schedule
          </button>
          <button
            data-testid="admin-tab-requests"
            onClick={() => handleSelectTab("requests")}
            className="slot"
            data-selected={tab === "requests" ? "true" : "false"}
            style={{ padding: "0.55rem 1rem" }}
          >
            <Inbox className="w-4 h-4 inline mr-1" /> Requests
          </button>
          <button
            data-testid="admin-tab-promo"
            onClick={() => handleSelectTab("promo")}
            className="slot"
            data-selected={tab === "promo" ? "true" : "false"}
            style={{ padding: "0.55rem 1rem" }}
          >
            <Tag className="w-4 h-4 inline mr-1" /> Promo Codes
          </button>
          <button
            data-testid="admin-tab-reviews"
            onClick={() => handleSelectTab("reviews")}
            className="slot relative"
            data-selected={tab === "reviews" ? "true" : "false"}
            style={{ padding: "0.55rem 1rem" }}
          >
            <MessageSquareQuote className="w-4 h-4 inline mr-1" /> Reviews
            <NotifBadge count={pendingReviewsCount} testId="badge-reviews" />
          </button>
        </div>

        {tab === "schedule" && <ScheduleManager />}
        {tab === "requests" && <RequestsTab />}
        {tab === "promo" && <PromoCodesTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "bookings" && (
          <>
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Jobs today"
            value={stats?.today_count ?? "—"}
            icon={CalendarDays}
            tint="#00E676"
          />
          <StatCard
            label="This week"
            value={stats?.week_count ?? "—"}
            icon={Users}
            tint="#00E5FF"
          />
          <StatCard
            label="Week revenue"
            value={stats ? `$${stats.week_revenue.toFixed(0)}` : "—"}
            icon={DollarSign}
            tint="#FFC107"
          />
          <StatCard
            label="Upcoming"
            value={stats?.upcoming_count ?? "—"}
            icon={CalendarDays}
            tint="#FF3D00"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-5">
          {[
            { k: "today", label: "Today" },
            { k: "upcoming", label: "Upcoming" },
            { k: "all", label: "All" },
            { k: "completed", label: "Completed" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`admin-filter-${f.k}`}
              onClick={() => setDateFilter(f.k)}
              className="slot"
              data-selected={dateFilter === f.k ? "true" : "false"}
              style={{ padding: "0.45rem 1rem" }}
            >
              {f.label}
            </button>
          ))}
          <button
            data-testid="admin-new-booking-btn"
            onClick={() => setNewBookingOpen(true)}
            className="comic-btn comic-btn-action"
            style={{ padding: "0.5rem 1rem", fontSize: "0.95rem", marginLeft: "auto" }}
          >
            <PlusCircle className="w-4 h-4" /> New Booking
          </button>
          <button
            data-testid="admin-refresh-btn"
            onClick={() => setRefreshFlag((x) => x + 1)}
            className="comic-btn comic-btn-outline"
            style={{ padding: "0.45rem 0.9rem", fontSize: "0.95rem" }}
          >
            Refresh
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-[#0B132B]/80">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && appointments.length === 0 && (
          <div className="comic-card-static p-6 text-center text-[#0B132B]/70">
            No bookings here yet.
          </div>
        )}

        {!loading &&
          Object.entries(grouped).map(([day, list]) => (
            <div key={day} className="mb-6">
              <div className="inline-block tape px-3 py-1 rotate-n2 mb-3">
                <span className="font-['Bebas_Neue'] text-lg text-[#0B132B]">{day}</span>
              </div>
              <div className="space-y-3">
                {list.map((a) => (
                  <AppointmentRow
                    key={a.id}
                    a={a}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                    onComplete={handleComplete}
                    onReopen={handleReopen}
                    onReschedule={(appt) => setRescheduleTarget(appt)}
                  />
                ))}
              </div>
            </div>
          ))}
          </>
        )}
      </main>
      <NewBookingModal
        open={newBookingOpen}
        onOpenChange={setNewBookingOpen}
        onCreated={() => setRefreshFlag((x) => x + 1)}
      />
      <RescheduleModal
        appt={rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        onRescheduled={() => setRefreshFlag((x) => x + 1)}
      />
    </div>
  );
}

const SERVICE_TYPE_OPTIONS = [
  { k: "bin_cleaning", label: "Bin Cleaning" },
  { k: "landscaping", label: "Landscaping / Yard Work" },
  { k: "weed_pulling", label: "Weed Pulling" },
  { k: "power_washing", label: "Power Washing" },
  { k: "leaf_raking", label: "Leaf Raking" },
  { k: "snow_shoveling", label: "Snow Shoveling" },
  { k: "other", label: "Other" },
];
const SERVICE_TYPE_LABELS = SERVICE_TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.k] = o.label;
  return acc;
}, {});

const REQUEST_STATUS_OPTIONS = [
  { k: "new", label: "New" },
  { k: "contacted", label: "Contacted" },
  { k: "scheduled", label: "Scheduled" },
  { k: "done", label: "Done" },
  { k: "archived", label: "Archived" },
];

function RequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [refreshFlag, setRefreshFlag] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    axios
      .get(`${API}/admin/service-requests`, { headers: authHeaders(), params })
      .then((r) => mounted && setRequests(r.data || []))
      .catch(() => toast.error("Failed to load requests"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [statusFilter, refreshFlag]);

  const updateStatus = async (id, status) => {
    try {
      await axios.post(
        `${API}/admin/service-requests/${id}/status`,
        { status },
        { headers: authHeaders() },
      );
      toast.success("Updated");
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not update");
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Permanently delete this request?")) return;
    try {
      await axios.delete(`${API}/admin/service-requests/${id}`, {
        headers: authHeaders(),
      });
      toast.success("Deleted");
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="admin-requests-tab">
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        <button
          onClick={() => setStatusFilter("all")}
          className="slot"
          data-selected={statusFilter === "all" ? "true" : "false"}
          style={{ padding: "0.45rem 1rem" }}
          data-testid="req-filter-all"
        >
          All
        </button>
        {REQUEST_STATUS_OPTIONS.map((o) => (
          <button
            key={o.k}
            onClick={() => setStatusFilter(o.k)}
            className="slot"
            data-selected={statusFilter === o.k ? "true" : "false"}
            style={{ padding: "0.45rem 1rem" }}
            data-testid={`req-filter-${o.k}`}
          >
            {o.label}
          </button>
        ))}
        <button
          onClick={() => setRefreshFlag((x) => x + 1)}
          className="comic-btn comic-btn-outline"
          style={{ padding: "0.45rem 0.9rem", fontSize: "0.95rem", marginLeft: "auto" }}
          data-testid="req-refresh-btn"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[#0B132B]/80">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}
      {!loading && requests.length === 0 && (
        <div className="comic-card-static p-6 text-center text-[#0B132B]/70">
          No service requests yet.
        </div>
      )}
      <div className="space-y-3">
        {requests.map((r) => {
          const label =
            SERVICE_TYPE_LABELS[r.service_type] || r.service_type || "Other";
          const created = new Date(r.created_at);
          return (
            <div
              key={r.id}
              data-testid={`req-row-${r.id}`}
              className="comic-card-static p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-['Bebas_Neue'] text-2xl leading-tight">
                      {label}
                    </span>
                    <span
                      className="text-[10px] uppercase font-bold px-2 py-0.5 rounded border-[2px] border-[#0B132B]"
                      style={{
                        background:
                          r.status === "new"
                            ? "#00E676"
                            : r.status === "contacted"
                              ? "#FFC107"
                              : r.status === "scheduled"
                                ? "#00E5FF"
                                : r.status === "done"
                                  ? "#1E88E5"
                                  : "#cbd5e1",
                        color: "#0B132B",
                      }}
                    >
                      {r.status}
                    </span>
                  </div>
                  <div className="text-sm font-semibold mt-1">
                    {r.customer_name}
                  </div>
                  <div className="text-xs text-[#0B132B]/70 flex flex-wrap gap-x-3 gap-y-1 mt-1">
                    <a
                      href={`tel:${r.customer_phone?.replace(/\D/g, "")}`}
                      className="inline-flex items-center gap-1 underline"
                    >
                      <Phone className="w-3.5 h-3.5" /> {r.customer_phone}
                    </a>
                    <a
                      href={`mailto:${r.customer_email}`}
                      className="inline-flex items-center gap-1 underline"
                    >
                      <Mail className="w-3.5 h-3.5" /> {r.customer_email}
                    </a>
                    {r.address && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" /> {r.address}
                        {r.city ? `, ${r.city}, CT` : ""}
                      </span>
                    )}
                  </div>
                  {r.preferred_date && (
                    <div className="text-xs mt-1">
                      <span className="font-semibold">Preferred:</span>{" "}
                      {r.preferred_date}
                    </div>
                  )}
                  <div className="text-sm italic mt-2 text-[#0B132B]/85 whitespace-pre-wrap">
                    "{r.description}"
                  </div>
                  <div className="text-[11px] text-[#0B132B]/60 mt-1">
                    Sent {created.toLocaleString()}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <select
                    value={r.status}
                    onChange={(e) => updateStatus(r.id, e.target.value)}
                    className="comic-input"
                    style={{ padding: "0.35rem 0.55rem", fontSize: "0.85rem" }}
                    data-testid={`req-status-${r.id}`}
                  >
                    {REQUEST_STATUS_OPTIONS.map((o) => (
                      <option key={o.k} value={o.k}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => deleteRequest(r.id)}
                    className="text-xs font-semibold inline-flex items-center gap-1 text-[#0B132B]/70 underline hover:text-[#FF3D00]"
                    data-testid={`req-delete-${r.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewBookingModal({ open, onOpenChange, onCreated }) {
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("Wethersfield");
  const [serviceType, setServiceType] = useState("bin_cleaning");
  const [serviceDescription, setServiceDescription] = useState("");
  const [numBins, setNumBins] = useState(1);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(120);
  const [totalAmount, setTotalAmount] = useState(15);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setSearchQ("");
      setResults([]);
      setSelected(null);
      setName("");
      setEmail("");
      setPhone("");
      setAddress("");
      setCity("Wethersfield");
      setServiceType("bin_cleaning");
      setServiceDescription("");
      setNumBins(1);
      const d = new Date();
      d.setDate(d.getDate() + 1);
      setDate(formatLocalDate(d));
      setTime("16:00");
      setDurationMinutes(120);
      setTotalAmount(15);
      setNotes("");
    }
  }, [open]);

  // Debounced customer search
  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/admin/customers/search`, {
          headers: authHeaders(),
          params: { q, limit: 8 },
        });
        setResults(r.data?.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  // Auto-adjust suggested total when service type or bins change (only if user hasn't typed)
  useEffect(() => {
    if (serviceType === "bin_cleaning") {
      const n = Math.max(1, parseInt(numBins, 10) || 1);
      const suggested = n <= 1 ? 15 : 15 + (n - 1) * 10;
      setTotalAmount(suggested);
      setDurationMinutes(120 + (n - 1) * 15);
    }
  }, [serviceType, numBins]);

  const selectCustomer = (c) => {
    setSelected(c);
    setName(c.name || "");
    setEmail(c.email || "");
    setPhone(c.phone || "");
    if (c.address) setAddress(c.address);
    if (c.city) setCity(c.city);
    setResults([]);
    setSearchQ("");
  };

  const canSubmit =
    name.trim().length >= 2 &&
    phone.replace(/\D/g, "").length >= 7 &&
    address.trim().length >= 3 &&
    date &&
    time &&
    Number(totalAmount) >= 0 &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const startAt = `${date}T${time}:00`;
      const payload = {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        address: address.trim(),
        city: city.trim() || "Wethersfield",
        service_type: serviceType,
        service_description:
          serviceType !== "bin_cleaning"
            ? serviceDescription.trim() || null
            : null,
        num_bins: serviceType === "bin_cleaning" ? Number(numBins) : 1,
        notes: notes.trim() || null,
        start_at: startAt,
        duration_minutes: Number(durationMinutes),
        total_amount: Number(totalAmount),
      };
      const trimEmail = email.trim();
      if (trimEmail) payload.customer_email = trimEmail;
      await axios.post(`${API}/admin/appointments`, payload, {
        headers: authHeaders(),
      });
      toast.success("Booking created");
      onCreated && onCreated();
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#FDFBF7] border-[4px] border-[#0B132B] rounded-2xl p-6 shadow-[8px_8px_0_#0B132B] max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="admin-new-booking-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-['Bebas_Neue'] text-3xl tracking-wide">
            New Booking
          </DialogTitle>
          <DialogDescription className="text-[#0B132B]/70">
            Add a job for a walk-up neighbor or an existing customer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          {/* Customer search */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              Find existing customer (optional)
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0B132B]/60" />
              <input
                data-testid="nb-customer-search"
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search by name, email, or phone…"
                className="comic-input"
                style={{ paddingLeft: "2rem" }}
              />
            </div>
            {searching && (
              <div className="text-xs text-[#0B132B]/60 mt-1">Searching…</div>
            )}
            {results.length > 0 && (
              <div className="mt-2 border-[3px] border-[#0B132B] rounded-lg bg-white shadow-[3px_3px_0_#0B132B] overflow-hidden">
                {results.map((c, i) => (
                  <button
                    key={`${c.email}-${i}`}
                    type="button"
                    onClick={() => selectCustomer(c)}
                    className="w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-[#FFF4C2]"
                    data-testid={`nb-customer-result-${i}`}
                  >
                    <div className="text-sm font-semibold">
                      {c.name || "—"}{" "}
                      <span className="text-xs text-[#0B132B]/60">
                        ({c.source})
                      </span>
                    </div>
                    <div className="text-xs text-[#0B132B]/70">
                      {c.email} · {c.phone || "no phone"}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {selected && (
              <div className="mt-2 text-xs inline-flex items-center gap-2 bg-[#00E676] border-[2px] border-[#0B132B] rounded-md px-2 py-1 shadow-[2px_2px_0_#0B132B]">
                Selected: {selected.name || selected.email}
                <button
                  type="button"
                  onClick={() => {
                    setSelected(null);
                    setName("");
                    setEmail("");
                    setPhone("");
                  }}
                  className="underline ml-1"
                >
                  clear
                </button>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Name *</label>
              <input
                data-testid="nb-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="comic-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Phone *</label>
              <input
                data-testid="nb-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="comic-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">
                Email (optional)
              </label>
              <input
                data-testid="nb-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="comic-input"
                placeholder="Leave blank for walk-ups"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">City</label>
              <input
                data-testid="nb-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="comic-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold mb-1">
                Service address *
              </label>
              <input
                data-testid="nb-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="comic-input"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Service type
            </label>
            <select
              data-testid="nb-service-type"
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              className="comic-input"
            >
              {SERVICE_TYPE_OPTIONS.map((o) => (
                <option key={o.k} value={o.k}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {serviceType === "bin_cleaning" ? (
            <div>
              <label className="block text-sm font-semibold mb-1">
                Number of bins
              </label>
              <input
                data-testid="nb-num-bins"
                type="number"
                inputMode="numeric"
                min={1}
                max={20}
                value={numBins}
                onChange={(e) => setNumBins(e.target.value)}
                onBlur={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isFinite(n) || n < 1) setNumBins(1);
                  else if (n > 20) setNumBins(20);
                }}
                className="comic-input"
                style={{ width: "8rem" }}
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold mb-1">
                Service details
              </label>
              <textarea
                data-testid="nb-service-description"
                value={serviceDescription}
                onChange={(e) => setServiceDescription(e.target.value)}
                rows={2}
                className="comic-textarea"
                placeholder="What does the job involve?"
              />
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Date *</label>
              <input
                data-testid="nb-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="comic-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Time *</label>
              <input
                data-testid="nb-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="comic-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">
                Duration (min)
              </label>
              <input
                data-testid="nb-duration"
                type="number"
                inputMode="numeric"
                min={15}
                max={480}
                step={15}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                onBlur={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isFinite(n) || n < 15) setDurationMinutes(60);
                  else if (n > 480) setDurationMinutes(480);
                }}
                className="comic-input"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">
                Total amount ($) *
              </label>
              <input
                data-testid="nb-total"
                type="number"
                min={0}
                step="0.01"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                className="comic-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Notes</label>
              <input
                data-testid="nb-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="comic-input"
                placeholder="Gate code, special instructions…"
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="comic-btn comic-btn-outline"
              style={{ padding: "0.55rem 1rem" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="comic-btn comic-btn-action"
              style={{ padding: "0.55rem 1rem" }}
              data-testid="nb-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </>
              ) : (
                <>Create Booking</>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Helper: read a file → compress to a max width, return data URL.
// Keeps the original aspect ratio (no cropping) so portrait photos don't get butchered.
async function fileToCompressedDataURL(file, maxWidth = 1600, quality = 0.85) {
  if (!file) return null;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, maxWidth / img.width);
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshFlag, setRefreshFlag] = useState(0);

  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [serviceType, setServiceType] = useState("bin_cleaning");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/gallery`)
      .then((r) => mounted && setItems(r.data || []))
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [refreshFlag]);

  const handleFile = async (e, which) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Pick an image file.");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      toast.error("Image too large (max 12MB).");
      return;
    }
    const dataUrl = await fileToCompressedDataURL(f);
    if (which === "before") {
      setBeforeFile(dataUrl);
      setBeforePreview(dataUrl);
    } else {
      setAfterFile(dataUrl);
      setAfterPreview(dataUrl);
    }
  };

  const reset = () => {
    setTitle("");
    setCaption("");
    setServiceType("bin_cleaning");
    setBeforeFile(null);
    setAfterFile(null);
    setBeforePreview(null);
    setAfterPreview(null);
  };

  const canSubmit =
    title.trim().length >= 1 && beforeFile && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Add a title and at least one photo.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/gallery`,
        {
          title: title.trim(),
          caption: caption.trim() || null,
          service_type: serviceType,
          before_image: beforeFile,
          after_image: afterFile || null,
        },
        { headers: authHeaders() },
      );
      toast.success("Added to gallery");
      reset();
      setRefreshFlag((x) => x + 1);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not upload");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers: authHeaders() });
      toast.success("Deleted");
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="admin-gallery-tab" className="space-y-6">
      <form
        onSubmit={submit}
        className="comic-card-static p-5 grid lg:grid-cols-2 gap-4"
        data-testid="gallery-upload-form"
      >
        <div className="lg:col-span-2 font-['Bebas_Neue'] text-2xl tracking-wide">
          Add Before / After
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Title *</label>
          <input
            data-testid="gallery-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="comic-input"
            placeholder="e.g. Toter bin, Wethersfield"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Service type</label>
          <select
            data-testid="gallery-service-type"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
            className="comic-input"
          >
            {SERVICE_TYPE_OPTIONS.map((o) => (
              <option key={o.k} value={o.k}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="lg:col-span-2">
          <label className="block text-sm font-semibold mb-1">Caption (optional)</label>
          <input
            data-testid="gallery-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="comic-input"
            placeholder="Quick description for the public page"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Before photo *</label>
          <div className="border-[3px] border-dashed border-[#0B132B] rounded-lg p-3 text-center bg-white">
            {beforePreview ? (
              <img
                src={beforePreview}
                alt="before preview"
                className="mx-auto max-h-44 rounded"
              />
            ) : (
              <div className="text-xs text-[#0B132B]/70 py-6">No photo yet</div>
            )}
            <label className="mt-2 inline-flex items-center gap-1 cursor-pointer comic-btn comic-btn-outline" style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}>
              <Upload className="w-3.5 h-3.5" /> Choose file
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFile(e, "before")}
                className="hidden"
                data-testid="gallery-before-input"
              />
            </label>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">After photo *</label>
          <div className="border-[3px] border-dashed border-[#0B132B] rounded-lg p-3 text-center bg-white">
            {afterPreview ? (
              <img
                src={afterPreview}
                alt="after preview"
                className="mx-auto max-h-44 rounded"
              />
            ) : (
              <div className="text-xs text-[#0B132B]/70 py-6">No photo yet</div>
            )}
            <label className="mt-2 inline-flex items-center gap-1 cursor-pointer comic-btn comic-btn-outline" style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}>
              <Upload className="w-3.5 h-3.5" /> Choose file
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFile(e, "after")}
                className="hidden"
                data-testid="gallery-after-input"
              />
            </label>
          </div>
        </div>

        <div className="lg:col-span-2 flex gap-2 justify-end">
          {(beforePreview || afterPreview || title || caption) && (
            <button
              type="button"
              onClick={reset}
              className="comic-btn comic-btn-outline"
              style={{ padding: "0.5rem 1rem" }}
            >
              Clear
            </button>
          )}
          <button
            type="submit"
            disabled={!canSubmit}
            className="comic-btn comic-btn-action"
            style={{ padding: "0.5rem 1rem" }}
            data-testid="gallery-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <PlusCircle className="w-4 h-4" /> Add to Gallery
              </>
            )}
          </button>
        </div>
      </form>

      <div>
        <div className="font-['Bebas_Neue'] text-2xl mb-3">Current Gallery</div>
        {loading && (
          <div className="flex items-center gap-2 text-[#0B132B]/80">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="comic-card-static p-6 text-center text-[#0B132B]/70">
            No gallery items yet. Add your first before/after above.
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div
              key={it.id}
              className="comic-card-static p-3"
              data-testid={`gallery-item-${it.id}`}
            >
              <div className="grid grid-cols-2 gap-1">
                <div className="relative">
                  <img
                    src={it.before_image}
                    alt={it.title}
                    className="w-full h-28 object-contain bg-[#0B132B] rounded border-[2px] border-[#0B132B]"
                  />
                  <span className="absolute top-1 left-1 text-[10px] font-bold bg-[#FF3D00] text-white px-1 rounded">
                    {it.after_image ? "BEFORE" : "PHOTO"}
                  </span>
                </div>
                {it.after_image ? (
                  <div className="relative">
                    <img
                      src={it.after_image}
                      alt="after"
                      className="w-full h-28 object-contain bg-[#0B132B] rounded border-[2px] border-[#0B132B]"
                    />
                    <span className="absolute top-1 left-1 text-[10px] font-bold bg-[#00E676] text-[#0B132B] px-1 rounded">
                      AFTER
                    </span>
                  </div>
                ) : (
                  <div className="w-full h-28 rounded border-[2px] border-dashed border-[#0B132B] grid place-items-center text-[10px] text-[#0B132B]/60 uppercase font-bold bg-white">
                    Single photo
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{it.title}</div>
                  {it.caption && (
                    <div className="text-xs text-[#0B132B]/70 truncate">{it.caption}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => remove(it.id)}
                  className="text-xs text-[#0B132B]/70 hover:text-[#FF3D00] inline-flex items-center gap-1"
                  data-testid={`gallery-delete-${it.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PromoCodesTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshFlag, setRefreshFlag] = useState(0);

  // New-code form
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [discountType, setDiscountType] = useState("flat_per_bin");
  const [value, setValue] = useState(10);
  const [maxUses, setMaxUses] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/admin/promo-codes`, { headers: authHeaders() })
      .then((r) => mounted && setCodes(r.data || []))
      .catch(() => mounted && setCodes([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [refreshFlag]);

  const reset = () => {
    setCode("");
    setDescription("");
    setDiscountType("flat_per_bin");
    setValue(10);
    setMaxUses("");
    setExpiresAt("");
  };

  const create = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error("Code is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        code: code.trim().toUpperCase(),
        description: description.trim() || null,
        discount_type: discountType,
        value: Number(value),
        max_uses: maxUses ? parseInt(maxUses, 10) : null,
        expires_at: expiresAt ? `${expiresAt}T23:59:59` : null,
        active: true,
      };
      await axios.post(`${API}/admin/promo-codes`, payload, {
        headers: authHeaders(),
      });
      toast.success("Promo code created");
      reset();
      setRefreshFlag((x) => x + 1);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create promo code");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (pc) => {
    try {
      await axios.patch(
        `${API}/admin/promo-codes/${pc.code}`,
        { active: !pc.active },
        { headers: authHeaders() },
      );
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (pc) => {
    if (!window.confirm(`Delete promo code ${pc.code}?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${pc.code}`, {
        headers: authHeaders(),
      });
      toast.success("Deleted");
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not delete");
    }
  };

  const describeDiscount = (pc) => {
    if (pc.discount_type === "flat_per_bin") return `$${pc.value} per bin`;
    if (pc.discount_type === "percent") return `${pc.value}% off`;
    if (pc.discount_type === "fixed") return `$${pc.value} off`;
    return "—";
  };

  return (
    <div data-testid="admin-promo-tab" className="space-y-6">
      <form
        onSubmit={create}
        data-testid="promo-create-form"
        className="comic-card-static p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        <div className="sm:col-span-2 lg:col-span-3 font-['Bebas_Neue'] text-2xl tracking-wide">
          Create Promo Code
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Code *</label>
          <input
            data-testid="promo-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. WELCOME10"
            className="comic-input uppercase tracking-wider"
            maxLength={40}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Discount type</label>
          <select
            data-testid="promo-type"
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value)}
            className="comic-input"
          >
            <option value="flat_per_bin">Flat $ per bin (e.g. $10/bin)</option>
            <option value="percent">Percent off</option>
            <option value="fixed">Fixed $ off total</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">
            Value{" "}
            <span className="text-[#0B132B]/60 font-normal">
              {discountType === "percent" ? "(%)" : "($)"}
            </span>
          </label>
          <input
            data-testid="promo-value"
            type="number"
            min={0}
            step={discountType === "percent" ? 1 : 0.5}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="comic-input"
            required
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-semibold mb-1">
            Description <span className="text-[#0B132B]/60 font-normal">(shown to customer)</span>
          </label>
          <input
            data-testid="promo-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Returning customer pricing"
            className="comic-input"
            maxLength={200}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Max uses</label>
          <input
            data-testid="promo-max-uses"
            type="number"
            min={1}
            placeholder="Unlimited"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            className="comic-input"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Expires on</label>
          <input
            data-testid="promo-expires"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="comic-input"
          />
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={submitting}
            data-testid="promo-submit"
            className="comic-btn comic-btn-action w-full"
            style={{ padding: "0.55rem 1rem" }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Creating…
              </>
            ) : (
              <>
                <PlusCircle className="w-4 h-4" /> Create Code
              </>
            )}
          </button>
        </div>
      </form>

      <div>
        <div className="font-['Bebas_Neue'] text-2xl mb-3">Active Codes</div>
        {loading && (
          <div className="flex items-center gap-2 text-[#0B132B]/80">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && codes.length === 0 && (
          <div className="comic-card-static p-6 text-center text-[#0B132B]/70">
            No promo codes yet.
          </div>
        )}
        <div className="space-y-3">
          {codes.map((pc) => (
            <div
              key={pc.code}
              data-testid={`promo-row-${pc.code}`}
              className={`comic-card-static p-4 ${!pc.active ? "opacity-60" : ""}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-['Bebas_Neue'] text-2xl tracking-wide">
                    {pc.code}
                    <span
                      className="ml-2 text-xs font-bold uppercase px-2 py-0.5 rounded border-[2px] border-[#0B132B]"
                      style={{
                        background: pc.active ? "#00E676" : "#cbd5e1",
                        color: "#0B132B",
                      }}
                    >
                      {pc.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="text-sm font-semibold mt-1">{describeDiscount(pc)}</div>
                  {pc.description && (
                    <div className="text-xs text-[#0B132B]/80 mt-1">{pc.description}</div>
                  )}
                  <div className="text-[11px] text-[#0B132B]/60 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span>
                      Used: {pc.used_count}
                      {pc.max_uses ? ` / ${pc.max_uses}` : ""}
                    </span>
                    {pc.expires_at && (
                      <span>
                        Expires: {new Date(pc.expires_at).toLocaleDateString()}
                      </span>
                    )}
                    <span>
                      Created:{" "}
                      {pc.created_at
                        ? new Date(pc.created_at).toLocaleDateString()
                        : "—"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => toggleActive(pc)}
                    data-testid={`promo-toggle-${pc.code}`}
                    className="text-xs font-semibold underline text-[#0B132B]/80 hover:text-[#0B132B]"
                  >
                    {pc.active ? "Disable" : "Enable"}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(pc)}
                    data-testid={`promo-delete-${pc.code}`}
                    className="text-xs font-semibold inline-flex items-center gap-1 text-[#0B132B]/70 underline hover:text-[#FF3D00]"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminStars({ rating }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${rating} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className="w-4 h-4"
          style={{
            fill: n <= rating ? "#FFC107" : "transparent",
            stroke: "#0B132B",
            strokeWidth: 2.5,
          }}
        />
      ))}
    </span>
  );
}

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [refreshFlag, setRefreshFlag] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    axios
      .get(`${API}/admin/reviews`, { headers: authHeaders(), params })
      .then((r) => mounted && setReviews(r.data || []))
      .catch(() => toast.error("Failed to load reviews"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [statusFilter, refreshFlag]);

  const setStatus = async (id, status) => {
    try {
      await axios.post(
        `${API}/admin/reviews/${id}/status`,
        { status },
        { headers: authHeaders() },
      );
      toast.success(
        status === "approved"
          ? "Published to landing page"
          : status === "hidden"
            ? "Hidden from landing page"
            : "Marked pending",
      );
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not update");
    }
  };

  const move = async (id, direction) => {
    try {
      const r = await axios.post(
        `${API}/admin/reviews/${id}/move`,
        { direction },
        { headers: authHeaders() },
      );
      if (r.data?.moved) {
        setRefreshFlag((x) => x + 1);
      }
    } catch {
      toast.error("Could not reorder");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this review?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: authHeaders() });
      toast.success("Deleted");
      setRefreshFlag((x) => x + 1);
    } catch {
      toast.error("Could not delete");
    }
  };

  const FILTERS = [
    { k: "pending", label: "Pending" },
    { k: "approved", label: "Published" },
    { k: "hidden", label: "Hidden" },
    { k: "all", label: "All" },
  ];

  return (
    <div data-testid="admin-reviews-tab">
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        {FILTERS.map((f) => (
          <button
            key={f.k}
            onClick={() => setStatusFilter(f.k)}
            className="slot"
            data-selected={statusFilter === f.k ? "true" : "false"}
            style={{ padding: "0.45rem 1rem" }}
            data-testid={`reviews-filter-${f.k}`}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={() => setRefreshFlag((x) => x + 1)}
          className="comic-btn comic-btn-outline"
          style={{ padding: "0.45rem 0.9rem", fontSize: "0.95rem" }}
          data-testid="reviews-refresh-btn"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[#0B132B]/80">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}
      {!loading && reviews.length === 0 && (
        <div className="comic-card-static p-6 text-center text-[#0B132B]/70">
          No reviews in this filter.
        </div>
      )}
      <div className="space-y-3">
        {reviews.map((r, i) => {
          const created = new Date(r.created_at);
          const isApproved = r.status === "approved";
          const isPending = r.status === "pending";
          const isHidden = r.status === "hidden";
          const showReorder = isApproved && statusFilter === "approved";
          return (
            <div
              key={r.id}
              data-testid={`review-row-${r.id}`}
              className="comic-card-static p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <AdminStars rating={r.rating} />
                    <span
                      className="text-[10px] uppercase font-bold px-2 py-0.5 rounded border-[2px] border-[#0B132B]"
                      style={{
                        background: isApproved
                          ? "#00E676"
                          : isPending
                            ? "#FFC107"
                            : "#cbd5e1",
                        color: "#0B132B",
                      }}
                    >
                      {r.status}
                    </span>
                    {showReorder && (
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-[#0B132B] text-white">
                        #{i + 1}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-semibold mt-2">{r.customer_name}</div>
                  {r.city && (
                    <div className="text-xs text-[#0B132B]/60">{r.city}, CT</div>
                  )}
                  <div className="text-sm italic mt-2 text-[#0B132B]/85 whitespace-pre-wrap">
                    "{r.comment}"
                  </div>
                  <div className="text-[11px] text-[#0B132B]/60 mt-1">
                    Submitted {created.toLocaleString()}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {showReorder && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => move(r.id, "up")}
                        disabled={i === 0}
                        data-testid={`review-up-${r.id}`}
                        className="comic-btn comic-btn-outline disabled:opacity-30"
                        style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }}
                        aria-label="Move up"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => move(r.id, "down")}
                        disabled={i === reviews.length - 1}
                        data-testid={`review-down-${r.id}`}
                        className="comic-btn comic-btn-outline disabled:opacity-30"
                        style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }}
                        aria-label="Move down"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {isPending && (
                    <button
                      onClick={() => setStatus(r.id, "approved")}
                      className="text-xs font-bold inline-flex items-center gap-1 bg-[#00E676] text-[#0B132B] border-[2px] border-[#0B132B] rounded px-2 py-1 shadow-[2px_2px_0_#0B132B]"
                      data-testid={`review-approve-${r.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Publish
                    </button>
                  )}
                  {isApproved && (
                    <button
                      onClick={() => setStatus(r.id, "hidden")}
                      className="text-xs font-bold inline-flex items-center gap-1 bg-white border-[2px] border-[#0B132B] rounded px-2 py-1 shadow-[2px_2px_0_#0B132B]"
                      data-testid={`review-hide-${r.id}`}
                    >
                      <EyeOff className="w-3.5 h-3.5" /> Hide
                    </button>
                  )}
                  {isHidden && (
                    <button
                      onClick={() => setStatus(r.id, "approved")}
                      className="text-xs font-bold inline-flex items-center gap-1 bg-[#00E676] text-[#0B132B] border-[2px] border-[#0B132B] rounded px-2 py-1 shadow-[2px_2px_0_#0B132B]"
                      data-testid={`review-unhide-${r.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Republish
                    </button>
                  )}
                  <button
                    onClick={() => remove(r.id)}
                    className="text-xs font-semibold inline-flex items-center gap-1 text-[#0B132B]/70 underline hover:text-[#FF3D00]"
                    data-testid={`review-delete-${r.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RescheduleModal({ appt, onClose, onRescheduled }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(120);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!appt) return;
    try {
      const d = new Date(appt.start_at);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      setDate(`${yyyy}-${mm}-${dd}`);
      setTime(
        `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
      );
      setDurationMinutes(appt.block_minutes || 120);
    } catch {
      /* noop */
    }
  }, [appt]);

  const open = Boolean(appt);
  const canSubmit = date && time && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/appointments/${appt.id}/reschedule`,
        {
          start_at: `${date}T${time}:00`,
          duration_minutes: Number(durationMinutes) || 120,
        },
        { headers: authHeaders() },
      );
      toast.success("Rescheduled");
      onRescheduled && onRescheduled();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not reschedule");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#FDFBF7] border-[4px] border-[#0B132B] rounded-2xl p-6 shadow-[8px_8px_0_#0B132B] max-w-md"
        data-testid="admin-reschedule-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-['Bebas_Neue'] text-3xl tracking-wide">
            Reschedule Appointment
          </DialogTitle>
          <DialogDescription className="text-[#0B132B]/70">
            {appt?.customer_name} · {appt?.address}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Date *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="comic-input"
                required
                data-testid="resched-date"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Time *</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="comic-input"
                required
                data-testid="resched-time"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Duration (min)
            </label>
            <input
              type="number"
              inputMode="numeric"
              min={15}
              max={480}
              step={15}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              onBlur={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!Number.isFinite(n) || n < 15) setDurationMinutes(60);
                else if (n > 480) setDurationMinutes(480);
              }}
              className="comic-input"
              style={{ width: "8rem" }}
              data-testid="resched-duration"
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="comic-btn comic-btn-outline"
              style={{ padding: "0.55rem 1rem" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="comic-btn comic-btn-action"
              style={{ padding: "0.55rem 1rem" }}
              data-testid="resched-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </>
              ) : (
                <>Save</>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null); // null = checking
  useEffect(() => {
    document.title = "Admin · Daniel's Dirty Deeds";
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    axios
      .get(`${API}/admin/me`, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => {
        setToken(null);
        setAuthed(false);
      });
  }, [navigate]);

  if (authed === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FDFBF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E88E5]" />
      </div>
    );
  }
  if (!authed) return <LoginCard onLoggedIn={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => setAuthed(false)} />;
}
